import { useState } from 'react';
import { useLocation } from 'react-router';

import { Title } from '../../utils/title';
import Base from '../Base';
import styles from './Unsubscribe.module.scss';

const API = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

/**
 * Login-less email unsubscribe page.
 *
 * The daily-digest email footer links here (e.g.
 * https://www.chimeable.com/unsubscribe?t=<token>). The token is an opaque,
 * signed value that encodes only the user's id; clicking the button POSTs it
 * to the members API, which flips the global email opt-out. Mirrors the
 * backend's "GET never mutates" rule — nothing happens until the button is
 * pressed, so a forward / scanner / link-preview prefetch can't unsubscribe.
 */
const Unsubscribe = () => {

  const { search } = useLocation();
  const token = new URLSearchParams(search).get('t') || '';

  // idle -> loading -> done | error
  const [ status, setStatus ] = useState(token ? 'idle' : 'invalid');

  const submit = async () => {
    setStatus('loading');
    try {
      const res = await fetch(
        `${API}/api/v1/unsubscribe/?t=${encodeURIComponent(token)}`,
        { method: 'POST' },
      );
      setStatus(res.ok ? 'done' : 'error');
    } catch (e) {
      setStatus('error');
    }
  };

  return (
    <Base>
      <Title value='Unsubscribe' />

      <div className={styles.root}>
        <div className={styles.card}>

          {
            status === 'invalid' &&
            <p className={styles.message}>
              This unsubscribe link is invalid or has expired.
            </p>
          }

          {
            (status === 'idle' || status === 'loading') &&
            <>
              <h1 className={styles.title}>Unsubscribe</h1>
              <p className={styles.message}>
                Click the button below to unsubscribe from all Chimeable emails.
              </p>
              <button
                className={styles.button}
                disabled={status === 'loading'}
                aria-label='unsubscribe'
                onClick={submit}>
                {status === 'loading' ? 'Unsubscribing…' : 'Unsubscribe'}
              </button>
            </>
          }

          {
            status === 'done' &&
            <p className={styles.message}>
              You have been unsubscribed from Chimeable emails.
            </p>
          }

          {
            status === 'error' &&
            <>
              <p className={styles.message}>
                Something went wrong. Please try again.
              </p>
              <button
                className={styles.button}
                aria-label='retry unsubscribe'
                onClick={submit}>
                Try again
              </button>
            </>
          }

        </div>
      </div>
    </Base>
  );
};

export default Unsubscribe;
